import { AnyAction, Reducer } from 'redux'
import { ActionType } from 'redux-promise-middleware'

import { PendingCounterState } from 'Models'

/**
 * Creates a reducer that counts pending promises.
 * @param actionTypes A list of base action types that should be reacted on. When omitted the reducer will react to all promise actions.
 * @returns the counter reducer
 *
 * `meta.skipLoadingIndicator` is only honored for this reducer's matching pending, fulfilled, and
 * rejected actions. Set it on the original promise action so redux-promise-middleware copies it to
 * that whole triple; mixing a counted pending with a skipped fulfilled/rejected leaks the counter.
 * Rejected actions with this flag are also ignored by the global error reducer.
 */
export const createPendingCounterReducer = (...actionTypes: string[]): Reducer<PendingCounterState> => {
  const actionTypesPatter = actionTypes.length === 0 ? '.+?' : `(${actionTypes.join(`|`)})`
  const incremenetActionRegex = new RegExp(`^${actionTypesPatter}_${ActionType.Pending}`)
  const decremenetActionRegex = new RegExp(`^${actionTypesPatter}_(${ActionType.Fulfilled}|${ActionType.Rejected})`)

  return (state: PendingCounterState = 0, { type, meta }: AnyAction): PendingCounterState => {
    if (incremenetActionRegex.test(type)) {
      return meta?.skipLoadingIndicator ? state : state + 1
    }

    if (decremenetActionRegex.test(type)) {
      return meta?.skipLoadingIndicator ? state : Math.max(state - 1, 0)
    }

    return state
  }
}

export default createPendingCounterReducer()
